// © 2025 GridStorm / Tekivex — All Rights Reserved
// Unauthorized reproduction or distribution is prohibited.
/**
 * Investigator — turns an anomaly event into a grounded Finding.
 *
 * Deterministic and AI-free: computes real statistics and cross-column
 * correlation, then templates a clear explanation. The enrichment step
 * (enrich.ts) is optional and only rewrites the prose.
 */

import type { Finding, FindingSeverity, Evidence, InvestigationInput } from './types.js';
import { getColumnStats, numericColumns, strongestCorrelate, isMeaningfulCorrelation } from './tools.js';

let _seq = 0;
function nextId(): string {
  const t = typeof performance !== 'undefined' ? Math.round(performance.now()) : Date.now();
  return `find-${++_seq}-${t}`;
}

function severityFromZ(z: number): FindingSeverity {
  const a = Math.abs(z);
  if (a >= 4) return 'critical';
  if (a >= 3) return 'warning';
  return 'info';
}

function confidence(z: number, sampleCount: number): number {
  const zPart = Math.min(1, Math.abs(z) / 6);
  const nPart = Math.min(1, sampleCount / 100);
  return Math.round(Math.min(0.99, 0.4 * nPart + 0.6 * zPart) * 100) / 100;
}

function fmt(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (Number.isInteger(n)) return n.toLocaleString('en-US');
  return n.toFixed(2);
}

/** Investigate one anomaly. Pure function — no I/O, no AI. */
export function investigate(input: InvestigationInput): Finding {
  const { anomaly, rows } = input;
  const col = anomaly.columnId;
  const value = anomaly.value;

  const z = anomaly.zScore ?? (anomaly.stats.stddev > 0
    ? (value - anomaly.stats.mean) / anomaly.stats.stddev
    : 0);
  const direction = value >= anomaly.stats.mean ? 'above' : 'below';

  // Correlation evidence is computed over the buffer EXCLUDING the anomaly
  // row — a large spike in the final row distorts r and would let the anomaly
  // manufacture its own "relationship". The companion's same-moment movement
  // (relVal) is still read from the anomaly row, which is the point of it.
  const baselineRows = rows.length > 1 ? rows.slice(0, -1) : rows;
  const numCols = numericColumns(baselineRows);
  const correlate = strongestCorrelate(baselineRows, col, numCols);

  let relatedNote = '';
  let relatedColumn: string | undefined;
  if (correlate && isMeaningfulCorrelation(correlate.r, correlate.n)) {
    relatedColumn = correlate.column;
    const rTag = `r=${correlate.r.toFixed(2)}, n=${correlate.n}`;
    const relStats = getColumnStats(baselineRows, correlate.column);
    const last = rows[rows.length - 1];
    const relVal = last ? last[correlate.column] : undefined;
    if (typeof relVal === 'number' && relStats.stddev > 0) {
      const relZ = (relVal - relStats.mean) / relStats.stddev;
      const moved = Math.abs(relZ) >= 1.5;
      relatedNote = moved
        ? ` Its usual companion ${correlate.column} (${rTag}) moved too (${relZ >= 0 ? '+' : ''}${relZ.toFixed(1)}σ), so this looks like a correlated shift rather than an isolated glitch.`
        : ` Normally tracks ${correlate.column} (${rTag}), but ${correlate.column} stayed flat — the relationship broke, which often signals a data or sensor issue.`;
    } else {
      relatedNote = ` Normally correlates with ${correlate.column} (${rTag}).`;
    }
  }

  const severity = severityFromZ(z);
  const title = `${col} ${direction === 'above' ? 'spiked' : 'dropped'} ${Math.abs(z).toFixed(1)}σ ${direction} normal`;

  const cause =
    `${col} = ${fmt(value)} is ${Math.abs(z).toFixed(1)} standard deviations ${direction} its ` +
    `recent mean of ${fmt(anomaly.stats.mean)} (σ = ${fmt(anomaly.stats.stddev)}, ` +
    `range ${fmt(anomaly.stats.min)}–${fmt(anomaly.stats.max)}).${relatedNote}`;

  const evidence: Evidence[] = [
    { label: 'Value',     value: fmt(value) },
    { label: 'Deviation', value: `${Math.abs(z).toFixed(1)}σ ${direction}` },
    { label: 'Mean',      value: fmt(anomaly.stats.mean) },
    { label: 'σ',         value: fmt(anomaly.stats.stddev) },
    { label: 'Samples',   value: String(anomaly.stats.sampleCount) },
  ];
  if (relatedColumn && correlate) {
    evidence.push({ label: 'Correlates with', value: `${relatedColumn} (r=${correlate.r.toFixed(2)}, n=${correlate.n})` });
  }

  return {
    id:        nextId(),
    timestamp: anomaly.timestamp,
    severity,
    title,
    column:    col,
    value,
    cause,
    evidence,
    relatedColumn,
    suggestedAction: defaultAction(severity, col, relatedColumn),
    confidence: confidence(z, anomaly.stats.sampleCount),
    enriched:  false,
  };
}

function defaultAction(sev: FindingSeverity, col: string, related?: string): string {
  if (sev === 'critical') {
    return related
      ? `Investigate ${col} and ${related} together now — a correlated critical shift usually has one upstream cause.`
      : `Investigate ${col} now — this is a critical outlier well outside its normal range.`;
  }
  if (sev === 'warning') return `Watch ${col} over the next few intervals; if it persists, escalate.`;
  return `Likely noise — no action needed unless it recurs.`;
}

/** Compact JSON context an AI provider can use to enrich the finding. */
export function enrichmentContext(input: InvestigationInput, finding: Finding): string {
  const { anomaly } = input;
  return JSON.stringify({
    anomaly: {
      column: anomaly.columnId,
      value: anomaly.value,
      mean: anomaly.stats.mean,
      stddev: anomaly.stats.stddev,
      zScore: finding.evidence.find((e) => e.label === 'Deviation')?.value,
      method: anomaly.method,
      sampleCount: anomaly.stats.sampleCount,
    },
    relatedColumn: finding.relatedColumn ?? null,
    severity: finding.severity,
  });
}
